/**
 * 🟦 V14.2 — Hook de estado centralizado para LyonHeaderButton.
 *
 * Extrae los useState de paneles secundarios del componente monolítico
 * `LyonHeaderButton.jsx` (1646 LOC → split). NO toca:
 *  - El estado del chat / mensajes / input
 *  - Posición del panel / minimized / drag-resize
 *  - Listeners postMessage / runGenioCommand
 *  - Cualquier handler de negocio
 *
 * Devuelve los mismos setters con los nombres legacy para mantener
 * compat total con el código existente — el refactor es PURO movimiento
 * de estado, sin cambios semánticos.
 */
import { useState, useCallback, useMemo } from 'react';

export function useLyonHeaderState() {
  // Paneles secundarios montados como siblings del panel Lyon.
  const [recipesPanelOpen, setRecipesPanelOpen] = useState(false);
  const [marketplaceOpen, setMarketplaceOpen] = useState(false);
  const [stylePacksOpen, setStylePacksOpen] = useState(false);
  const [saveRecipeModalOpen, setSaveRecipeModalOpen] = useState(false);
  const [inferenceModalOpen, setInferenceModalOpen] = useState(false);

  // Acciones idempotentes (toggle/open/close) por panel.
  const actions = useMemo(() => ({
    // recipes
    openRecipes: () => setRecipesPanelOpen(true),
    closeRecipes: () => setRecipesPanelOpen(false),
    toggleRecipes: () => setRecipesPanelOpen((v) => !v),
    // marketplace
    openMarketplace: () => setMarketplaceOpen(true),
    closeMarketplace: () => setMarketplaceOpen(false),
    toggleMarketplace: () => setMarketplaceOpen((v) => !v),
    // style packs
    openStylePacks: () => setStylePacksOpen(true),
    closeStylePacks: () => setStylePacksOpen(false),
    toggleStylePacks: () => setStylePacksOpen((v) => !v),
    // save recipe modal
    openSaveRecipe: () => setSaveRecipeModalOpen(true),
    closeSaveRecipe: () => setSaveRecipeModalOpen(false),
    // style inference modal
    openInference: () => setInferenceModalOpen(true),
    closeInference: () => setInferenceModalOpen(false),
  }), []);

  // Exponemos también los setters originales para no romper imports/calls
  // que ya existan en `LyonHeaderButton.jsx`.
  const state = {
    recipesPanelOpen,
    marketplaceOpen,
    stylePacksOpen,
    saveRecipeModalOpen,
    inferenceModalOpen,
  };

  const setters = {
    setRecipesPanelOpen,
    setMarketplaceOpen,
    setStylePacksOpen,
    setSaveRecipeModalOpen,
    setInferenceModalOpen,
  };

  return { state, setters, actions };
}

export default useLyonHeaderState;
